import TagManager from 'react-gtm-module';
import appConfig from 'api/appConfig';
import { analyticsInputChangeUtils } from './inputChangeUtils';
import {
    AnalyticsEvent,
    IAnalyticsApplication,
    IAnalyticsAdTrax,
    IAnalyticsAppAddressScrubbing,
    IAnalyticsFieldError,
    IAnalyticsBanner,
    IAnalyticsRiderDisplay,
    IAnalyticsProgressItemListClick,
    IAnalyticsPixelFire,
    IAnalyticsProductNotAvailable
} from './types';

type ApplicationEventDetail = IAnalyticsApplication & Partial<IAnalyticsProductNotAvailable>;

export class AnalyticsService {
    private static instance: AnalyticsService;

    constructor() {
        this.bootstrapGoogleTagManager();
        document.addEventListener(AnalyticsEvent.ADDRESS_SCRUBBING, ((e: CustomEvent) => {
            this.applicationAddressScrubbing(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.ADTRAX, ((e: CustomEvent) => {
            this.adTraxOnPage(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.APPLICATION_FUNNEL, ((e: CustomEvent) => {
            this.application(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.CHECKBOX_INTERACTION, ((e: CustomEvent) => {
            this.applicationInputChange(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.HEALTH_QUESTIONS, ((e: CustomEvent) => {
            this.applicationInputChange(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.HELP_TEXT, ((e: CustomEvent) => {
            this.applicationInputChange(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.INPUT, ((e: CustomEvent) => {
            this.applicationInputChange(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.OTHER_BUTTONS, ((e: CustomEvent) => {
            this.applicationInputChange(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.RIDER_DISPLAYED, ((e: CustomEvent) => {
            this.riderDisplay(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.RIDER_DISPLAYED_SELECTED, ((e: CustomEvent) => {
            this.riderDisplay(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.RIDER_REMOVED, ((e: CustomEvent) => {
            this.riderDisplay(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.RIDER_SELECTED, ((e: CustomEvent) => {
            this.riderDisplay(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.SELECTION, ((e: CustomEvent) => {
            this.applicationInputChange(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.FIELD_ERROR, ((e: CustomEvent) => {
            this.fieldError(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.ERROR_BANNER, ((e: CustomEvent) => {
            this.notificationBanner(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.ALERT_BANNER, ((e: CustomEvent) => {
            this.notificationBanner(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.USER_DATA, ((e: CustomEvent) => {
            this.userData(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.ERROR_LOADING_DOC, ((e: CustomEvent) => {
            this.application(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.ABANDON_APP, ((e: CustomEvent) => {
            this.application(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.PROGRESS_ITEM_LIST_BUTTON, ((e: CustomEvent) => {
            this.progressItemListButtonClick(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.PIXEL_FIRE, ((e: CustomEvent) => {
            this.startPixelFire(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.SAFETY_NET_TIMER_DISPLAYED, ((e: CustomEvent) => {
            this.basicEvent(e);
        }) as EventListener);
        document.addEventListener(AnalyticsEvent.SAFETY_NET_TIMER_UPDATED, ((e: CustomEvent) => {
            this.basicEvent(e);
        }) as EventListener);
    }

    /**
     * Use this to establish Analytics Service once per project.
     */
    public static init(): AnalyticsService {
        if (!AnalyticsService.instance) {
            AnalyticsService.instance = new AnalyticsService();
        }

        return AnalyticsService.instance;
    }

    private bootstrapGoogleTagManager = (): void => {
        const tagManagerArgs = {
            gtmId: appConfig.VITE_GOOGLE_TAG_MANAGER_ID || ''
        };
        if (tagManagerArgs.gtmId !== '') {
            TagManager.initialize(tagManagerArgs);
        }
    };

    private application = (
        event:
            | GlobalEventHandlersEventMap[AnalyticsEvent.APPLICATION_FUNNEL]
            | GlobalEventHandlersEventMap[AnalyticsEvent.ERROR_LOADING_DOC]
            | GlobalEventHandlersEventMap[AnalyticsEvent.ABANDON_APP]
    ): void => {
        const { detail } = event;
        const { channel, coverageAmount, policyStatus, premiumAmount, productCode, segmentTitle, pnaReason } =
            detail as ApplicationEventDetail;
        TagManager.dataLayer({
            dataLayer: {
                event: event.type,
                stepName: segmentTitle.replace('&', 'and'), // sending symbols in the data  might mess up things in GTM when using Regex comparisons
                channel,
                ...(policyStatus !== undefined && { policyStatus }),
                ...(pnaReason !== undefined && { pnaReason }),
                productCode,
                premiumAmount,
                coverageAmount
            }
        });
    };

    private basicEvent = (
        event:
            | GlobalEventHandlersEventMap[AnalyticsEvent.SAFETY_NET_TIMER_DISPLAYED]
            | GlobalEventHandlersEventMap[AnalyticsEvent.SAFETY_NET_TIMER_UPDATED]
    ): void => {
        TagManager.dataLayer({
            dataLayer: {
                event: event.type
            }
        });
    };

    private adTraxOnPage = (event: GlobalEventHandlersEventMap[AnalyticsEvent.ADTRAX]): void => {
        const { detail } = event;
        const { adTraxValue } = detail as IAnalyticsAdTrax;
        TagManager.dataLayer({
            dataLayer: {
                event: event.type,
                adTraxValue
            }
        });
    };

    private applicationInputChange = (
        event:
            | GlobalEventHandlersEventMap[AnalyticsEvent.CHECKBOX_INTERACTION]
            | GlobalEventHandlersEventMap[AnalyticsEvent.HEALTH_QUESTIONS]
            | GlobalEventHandlersEventMap[AnalyticsEvent.HELP_TEXT]
            | GlobalEventHandlersEventMap[AnalyticsEvent.INPUT]
            | GlobalEventHandlersEventMap[AnalyticsEvent.OTHER_BUTTONS]
            | GlobalEventHandlersEventMap[AnalyticsEvent.SELECTION]
            | GlobalEventHandlersEventMap[AnalyticsEvent.FIELD_ERROR]
            | GlobalEventHandlersEventMap[AnalyticsEvent.ERROR_BANNER]
    ): void => {
        const { detail, type } = event;
        TagManager.dataLayer({
            dataLayer: analyticsInputChangeUtils.getApplicationInputChangeDataLayerData(type, detail)
        });
    };

    private applicationAddressScrubbing = (
        event: GlobalEventHandlersEventMap[AnalyticsEvent.ADDRESS_SCRUBBING]
    ): void => {
        const { detail } = event;
        const { action, label } = detail as IAnalyticsAppAddressScrubbing;
        TagManager.dataLayer({
            dataLayer: {
                event: event.type,
                action,
                label
            }
        });
    };

    private fieldError = (event: GlobalEventHandlersEventMap[AnalyticsEvent.FIELD_ERROR]): void => {
        const { detail } = event;
        const {
            channel,
            coverageAmount,
            policyStatus,
            premiumAmount,
            productCode,
            segmentTitle,
            fieldName,
            errorValue
        } = detail as IAnalyticsFieldError;
        TagManager.dataLayer({
            dataLayer: {
                event: event.type,
                stepName: segmentTitle.replace('&', 'and'), // sending symbols in the data  might mess up things in GTM when using Regex comparisions
                channel,
                ...(policyStatus !== undefined && { policyStatus }),
                productCode,
                premiumAmount,
                coverageAmount,
                fieldName,
                errorValue
            }
        });
    };

    private notificationBanner = (
        event:
            | GlobalEventHandlersEventMap[AnalyticsEvent.ERROR_BANNER]
            | GlobalEventHandlersEventMap[AnalyticsEvent.ALERT_BANNER]
    ): void => {
        const { detail } = event;
        const {
            channel,
            coverageAmount,
            policyStatus,
            premiumAmount,
            productCode,
            segmentTitle,
            bannerMessage,
            fieldAction
        } = detail as IAnalyticsBanner;
        TagManager.dataLayer({
            dataLayer: {
                event: event.type,
                stepName: segmentTitle.replace('&', 'and'), // sending symbols in the data  might mess up things in GTM when using Regex comparisions
                channel,
                ...(policyStatus !== undefined && { policyStatus }),
                productCode,
                premiumAmount,
                coverageAmount,
                bannerMessage,
                ...(fieldAction !== undefined && { fieldAction })
            }
        });
    };

    private userData = (event: GlobalEventHandlersEventMap[AnalyticsEvent.USER_DATA]): void => {
        const { detail } = event;
        TagManager.dataLayer({
            dataLayer: {
                ...detail
            }
        });
    };

    private riderDisplay = (
        event:
            | GlobalEventHandlersEventMap[AnalyticsEvent.RIDER_DISPLAYED]
            | GlobalEventHandlersEventMap[AnalyticsEvent.RIDER_DISPLAYED_SELECTED]
            | GlobalEventHandlersEventMap[AnalyticsEvent.RIDER_REMOVED]
            | GlobalEventHandlersEventMap[AnalyticsEvent.RIDER_SELECTED]
    ): void => {
        const { detail, type } = event;
        const { coverageAmount, premiumAmount, productCode, segmentTitle } = detail as IAnalyticsRiderDisplay;
        TagManager.dataLayer({
            dataLayer: {
                'event': type,
                'stepName': segmentTitle.replace('&', 'and'),
                'rider.productCode': productCode,
                'rider.coverageAmount': coverageAmount,
                'rider.premiumAmount': premiumAmount
            }
        });
    };

    private progressItemListButtonClick = (
        event: GlobalEventHandlersEventMap[AnalyticsEvent.PROGRESS_ITEM_LIST_BUTTON]
    ): void => {
        const { detail } = event;
        const { channel, coverageAmount, premiumAmount, productCode, stepName } =
            detail as IAnalyticsProgressItemListClick;
        const { fieldName, fieldAction } = detail as IAnalyticsProgressItemListClick;
        TagManager.dataLayer({
            dataLayer: {
                event: event.type,
                fieldName,
                fieldAction,
                stepName: stepName.replace('&', 'and'),
                channel,
                productCode,
                premiumAmount,
                coverageAmount
            }
        });
    };

    private startPixelFire = (event: GlobalEventHandlersEventMap[AnalyticsEvent.PIXEL_FIRE]): void => {
        const { detail } = event;
        const { spotName, productCode } = detail as IAnalyticsPixelFire;
        TagManager.dataLayer({
            dataLayer: {
                event: event.type,
                spotName,
                productCode
            }
        });
    };
}
